import React, { useState } from "react";
import { useSeo } from "../useSeo";
import { trackEvent } from "../trackEvent";
import { API_BASE } from "../../api/client";

const EMPTY_FORM = {
  fullName: "",
  email: "",
  birthDate: "",
  cpf: "",
  mobilePhone: "",
  homePhone: "",
  emergencyContactName: "",
  emergencyPhone: "",
  address: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  zipCode: "",
  spouseName: "",
  fatherName: "",
  motherName: "",
  specialNeeds: "",
  baptismDate: "",
  notBaptized: false,
  baptismChurch: "",
  churchRole: "",
  howMetChurch: "",
  faithOriginProfile: "",
  gender: "",
  attendingSince: "",
  notes: ""
};

export default function CadastroMembro() {
  useSeo({
    title: "Cadastro de membro | Igreja Batista Mosaico",
    description: "Cadastro de membresia da Igreja Batista Mosaico -- preencha seus dados pra ficar registrado na igreja.",
    path: "/cadastro/membro"
  });

  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  async function handleSubmit(event) {
    event.preventDefault();
    trackEvent("form_submit", { form_name: "cadastro_membro" });

    if (!form.notBaptized && !form.baptismDate) {
      setStatus("error");
      setErrorMsg('Informe a data de batismo ou marque "ainda não fui batizado(a)".');
      return;
    }

    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/public/membro-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          fullName: form.fullName.trim(),
          birthDate: form.birthDate || null,
          baptismDate: form.notBaptized ? null : form.baptismDate || null,
          attendingSince: form.attendingSince || null
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Não foi possível enviar seu cadastro agora.");
      }
      setStatus("sent");
      setForm(EMPTY_FORM);

      // Depois do cadastro de membresia, leva a pessoa pra criar o login
      // dela no site (conta do app Familia Mosaico) -- pedido do Drigo
      // (17/10/2026): "ao se cadastrar como membro receber uma
      // interceptacao pra se cadastrar o user e login no site". A
      // aprovacao do cadastro de membresia continua normal, no admin, sem
      // nenhuma relacao direta com essa conta (so a secretaria vincula uma
      // coisa na outra depois, em /admin/usuarios). Preenche nome/e-mail/
      // whatsapp que a pessoa ja digitou aqui pra nao perguntar de novo.
      const redirectParams = new URLSearchParams({ novo_cadastro: "1" });
      if (form.fullName.trim()) redirectParams.set("nome", form.fullName.trim());
      if (form.email.trim()) redirectParams.set("email", form.email.trim());
      if (form.mobilePhone.trim()) redirectParams.set("whatsapp", form.mobilePhone.trim());
      window.setTimeout(() => {
        window.location.href = `/familia?${redirectParams.toString()}`;
      }, 1800);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message || "Não foi possível enviar seu cadastro agora. Tente novamente em instantes.");
    }
  }

  const disabled = status === "sending";

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Cadastro de membresia</p>
        <h1>Faça parte do cadastro da Mosaico</h1>
        <p>Preencha seus dados abaixo. Alguém da secretaria confere e confirma seu cadastro em seguida.</p>
      </div>

      <div className="card" style={{ maxWidth: 720 }}>
        {status === "sent" && (
          <div className="confirm-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            Recebemos seu cadastro -- a secretaria confirma em breve. Agora vamos te levar pra criar seu acesso ao site...
          </div>
        )}
        {status === "error" && (
          <div className="confirm-banner confirm-banner-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "0 0 10px" }}>Dados pessoais</p>
          <div className="form-row"><label>Nome completo <span className="form-required">*</span></label><input type="text" required placeholder="Seu nome completo" value={form.fullName} onChange={(e) => update("fullName", e.target.value)} disabled={disabled} /></div>
          <div className="grid grid-2">
            <div className="form-row"><label>Data de nascimento <span className="form-optional">(opcional)</span></label><input type="date" value={form.birthDate} onChange={(e) => update("birthDate", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>CPF <span className="form-optional">(opcional)</span></label><input type="text" placeholder="000.000.000-00" value={form.cpf} onChange={(e) => update("cpf", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row">
            <label>Sexo <span className="form-optional">(opcional)</span></label>
            <select value={form.gender} onChange={(e) => update("gender", e.target.value)} disabled={disabled}>
              <option value="">Prefiro não informar</option>
              <option value="MASCULINO">Masculino</option>
              <option value="FEMININO">Feminino</option>
              <option value="OUTRO">Outro</option>
            </select>
          </div>
          <div className="grid grid-2">
            <div className="form-row"><label>E-mail <span className="form-optional">(opcional)</span></label><input type="email" placeholder="voce@email.com" value={form.email} onChange={(e) => update("email", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>WhatsApp <span className="form-optional">(opcional)</span></label><input type="tel" placeholder="(11) 90000-0000" value={form.mobilePhone} onChange={(e) => update("mobilePhone", e.target.value)} disabled={disabled} /></div>
          </div>

          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 10px" }}>Endereço</p>
          <div className="form-row"><label>Endereço <span className="form-optional">(opcional)</span></label><input type="text" placeholder="Rua, número" value={form.address} onChange={(e) => update("address", e.target.value)} disabled={disabled} /></div>
          <div className="grid grid-2">
            <div className="form-row"><label>Complemento <span className="form-optional">(opcional)</span></label><input type="text" placeholder="Apto, bloco..." value={form.addressComplement} onChange={(e) => update("addressComplement", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Bairro <span className="form-optional">(opcional)</span></label><input type="text" value={form.neighborhood} onChange={(e) => update("neighborhood", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="grid grid-2">
            <div className="form-row"><label>Cidade <span className="form-optional">(opcional)</span></label><input type="text" value={form.city} onChange={(e) => update("city", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Estado (UF) <span className="form-optional">(opcional)</span></label><input type="text" maxLength={2} placeholder="SP" value={form.state} onChange={(e) => update("state", e.target.value.toUpperCase())} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>CEP <span className="form-optional">(opcional)</span></label><input type="text" placeholder="00000-000" value={form.zipCode} onChange={(e) => update("zipCode", e.target.value)} disabled={disabled} /></div>

          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 10px" }}>Contato de emergência e família</p>
          <div className="grid grid-2">
            <div className="form-row"><label>Nome do contato de emergência <span className="form-optional">(opcional)</span></label><input type="text" value={form.emergencyContactName} onChange={(e) => update("emergencyContactName", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Telefone de emergência <span className="form-optional">(opcional)</span></label><input type="tel" value={form.emergencyPhone} onChange={(e) => update("emergencyPhone", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>Cônjuge <span className="form-optional">(opcional)</span></label><input type="text" placeholder="Nome do cônjuge, se casado(a)" value={form.spouseName} onChange={(e) => update("spouseName", e.target.value)} disabled={disabled} /></div>
          <div className="grid grid-2">
            <div className="form-row"><label>Nome do pai <span className="form-optional">(opcional)</span></label><input type="text" value={form.fatherName} onChange={(e) => update("fatherName", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Nome da mãe <span className="form-optional">(opcional)</span></label><input type="text" value={form.motherName} onChange={(e) => update("motherName", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>Alguma necessidade especial de acessibilidade? <span className="form-optional">(opcional)</span></label><input type="text" value={form.specialNeeds} onChange={(e) => update("specialNeeds", e.target.value)} disabled={disabled} /></div>

          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 10px" }}>Vida na igreja</p>
          <div className="form-row">
            <label>Data de batismo <span className="form-required">*</span></label>
            <input type="date" value={form.baptismDate} onChange={(e) => update("baptismDate", e.target.value)} disabled={disabled || form.notBaptized} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, textTransform: "none", fontWeight: 500, fontSize: 13, color: "#c7d1e4" }}>
              <input
              type="checkbox"
              checked={form.notBaptized}
              onChange={(e) => {
                const checked = e.target.checked;
                setForm((prev) => ({
                  ...prev,
                  notBaptized: checked,
                  baptismDate: checked ? "" : prev.baptismDate,
                  baptismChurch: checked ? "" : prev.baptismChurch
                }));
              }}
              disabled={disabled}
              style={{ width: 16, height: 16 }}
            />
              Ainda não fui batizado(a)
            </label>
          </div>
          <div className="grid grid-2">
            <div className="form-row"><label>Igreja onde foi batizado(a) <span className="form-optional">(opcional)</span></label><input type="text" value={form.baptismChurch} onChange={(e) => update("baptismChurch", e.target.value)} disabled={disabled || form.notBaptized} /></div>
            <div className="form-row"><label>Membro/participante desde <span className="form-optional">(opcional)</span></label><input type="date" value={form.attendingSince} onChange={(e) => update("attendingSince", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row">
            <label>Como foi sua chegada na fé/igreja? <span className="form-optional">(opcional)</span></label>
            <select value={form.faithOriginProfile} onChange={(e) => update("faithOriginProfile", e.target.value)} disabled={disabled}>
              <option value="">Prefiro não dizer agora</option>
              <option value="DECIDIU_AGORA">Decidi seguir Jesus agora, pela primeira vez</option>
              <option value="OUTRA_IGREJA">Já era evangélico(a), vim de outra igreja</option>
              <option value="SEM_IGREJA">Já era evangélico(a), mas estava um tempo sem igreja</option>
              <option value="OUTRO">Outro</option>
            </select>
          </div>
          <div className="form-row"><label>Já exerce ou exerceu alguma função em outra igreja? <span className="form-optional">(opcional)</span></label><input type="text" value={form.churchRole} onChange={(e) => update("churchRole", e.target.value)} disabled={disabled} /></div>
          <div className="form-row"><label>Como conheceu a Mosaico? <span className="form-optional">(opcional)</span></label><input type="text" value={form.howMetChurch} onChange={(e) => update("howMetChurch", e.target.value)} disabled={disabled} /></div>
          <div className="form-row"><label>Alguma observação? <span className="form-optional">(opcional)</span></label><textarea rows="3" value={form.notes} onChange={(e) => update("notes", e.target.value)} disabled={disabled} /></div>

          <button className="submit-btn" type="submit" disabled={disabled}>{disabled ? "Enviando…" : "Enviar cadastro"}</button>
          <p className="form-note">Seu cadastro fica pendente até alguém da secretaria confirmar os dados.</p>
        </form>
      </div>
    </>
  );
}
